/**
 * ApprovalsPanel — Godkjenn-skjermen, uten sideramme (#155, redesign steg 5a).
 *
 * Skissen `Stell_Godkjenn`: én kø for alt som venter på en voksen, i stedet
 * for fanene Oppgaver / Belønninger og forslagsseksjonen over dem.
 *
 *   - Toppen: avataren (Meg), «Godkjenn», «N venter · eldste først», og en
 *     amber chip med hvor mange som har ventet over 24 timer.
 *   - Filterchips Alle / Oppgaver / Forslag / Bytter med antall.
 *   - Det første elementet vises stort, med handlingene: ApprovalCard
 *     (oppgave), ProposalCard (forslag) eller RewardClaimCard (bytte).
 *     Avvisning skjer inline i kortet.
 *   - Resten står i en kompakt liste («Også i køen»). En rad løfter
 *     elementet opp i det store kortet.
 *   - Under Bytter står skjemaet for nye belønninger, som før lå under
 *     Belønninger-fanen (RewardTemplateForm, D-11 Layer 1).
 *
 * Dyplenker: `?id=<id>` (push for oppgave til godkjenning, SLA-varslene og
 * belønningsbytter) løfter elementet med den id-en opp, scroller dit og
 * uthever det i 3 s. Det gjelder alle tre køene, så `?id=…&tab=rewards` fra
 * rewards.subscribers.ts lander nå på byttet (MAPPING.md §4).
 *
 * The panel assumes the caller has already established that the viewer is an
 * admin (ApprovalsRoute gates on /api/auth/me). The server is the
 * authoritative gate on every approve/reject regardless — requireAdminScope on
 * the route.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Card, Chip, EmptyState } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { InitialAvatar, asColorSlot } from '../../components/primitives/InitialAvatar';
import type { ColorSlot } from '../../components/primitives/PixelAvatar';
import { RelativeTime } from '../../components/primitives/RelativeTime';
import { MeAvatarLink } from '../../components/ScreenHeader';
import { FeedbackWidget } from '../feedback/FeedbackWidget';
import { useChoreActions } from '../chores/useChoreActions';
import { useFamilyMembers } from '../chores/useFamilyMembers';
import { RewardClaimCard } from '../rewards/RewardClaimCard';
import { RewardTemplateForm } from '../rewards/RewardTemplateForm';
import { useRewardsAdmin } from '../rewards/useRewardsQueries';
import { ApprovalCard, type MutateHandlers } from './ApprovalCard';
import { ProposalCard, recurrenceLabel } from './ProposalCard';
import { QueueRow, RewardTile, WaitChip } from './queueParts';
import { useApproveProposal, useRejectProposal, type ApproveProposalOverrides } from './useProposals';
import { useApprovalQueue, waitingHours, type QueueItem, type QueueKind } from './useApprovalQueue';

const HIGHLIGHT_MS = 3000;

const FILTERS = ['all', 'chore', 'proposal', 'reward'] as const;
type Filter = (typeof FILTERS)[number];

interface Person {
  name: string;
  slot: ColorSlot;
}

export interface ApprovalsPanelProps {
  /** True once the caller knows the viewer is an admin; gates the fetch. */
  isAdmin: boolean;
  /** Override clock for tests. */
  now?: Date;
}

export function ApprovalsPanel({ isAdmin, now }: ApprovalsPanelProps): ReactNode {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const wallClock = now ?? new Date();

  const { items, isLoading } = useApprovalQueue(isAdmin);
  const members = useFamilyMembers();
  const rewardsCatalog = useRewardsAdmin(isAdmin);
  const actions = useChoreActions();
  const approveProposal = useApproveProposal();
  const rejectProposal = useRejectProposal();

  const [filter, setFilter] = useState<Filter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const featuredRef = useRef<HTMLDivElement | null>(null);

  const people = useMemo(() => {
    const map = new Map<string, Person>();
    for (const m of members.data ?? []) {
      map.set(m.id, { name: m.short_name || m.name, slot: asColorSlot(m.color_token) });
    }
    return map;
  }, [members.data]);

  const rewardTitles = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rewardsCatalog.data ?? []) map.set(r.id, r.title);
    return map;
  }, [rewardsCatalog.data]);

  const counts = useMemo(() => {
    const c: Record<QueueKind, number> = { chore: 0, proposal: 0, reward: 0 };
    for (const i of items) c[i.kind] += 1;
    return c;
  }, [items]);

  const over24 = items.filter((i) => (waitingHours(i.at, wallClock) ?? 0) >= 24).length;
  const filtered = filter === 'all' ? items : items.filter((i) => i.kind === filter);
  const featured = filtered.find((i) => i.id === selectedId) ?? filtered[0] ?? null;
  const rest = filtered.filter((i) => i !== featured);

  // Dyplenke ?id= — én gang per id, ikke på hver ny henting av køen.
  const deepLinkId = searchParams.get('id');
  const handledDeepLink = useRef<string | null>(null);
  useEffect(() => {
    if (!deepLinkId || handledDeepLink.current === deepLinkId) return;
    if (!items.some((i) => i.id === deepLinkId)) return;
    handledDeepLink.current = deepLinkId;
    setFilter('all');
    setSelectedId(deepLinkId);
    setHighlightId(deepLinkId);
  }, [deepLinkId, items]);

  // Scroll + utheving når et element er dyplenket. Egen effekt, så tidtakeren
  // ikke nullstilles av at køen hentes på nytt.
  useEffect(() => {
    if (!highlightId) return undefined;
    // Ett tick, så kortet med utheving er tegnet før scrollIntoView.
    const scroll = window.setTimeout(() => {
      const el = featuredRef.current;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 0);
    const clear = window.setTimeout(() => setHighlightId(null), HIGHLIGHT_MS);
    return () => {
      window.clearTimeout(scroll);
      window.clearTimeout(clear);
    };
  }, [highlightId]);

  const select = (id: string): void => {
    setSelectedId(id);
    featuredRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const personFor = (id: string | null | undefined): Person | undefined =>
    id ? people.get(id) : undefined;

  const rewardTitle = (rewardId: string): string => rewardTitles.get(rewardId) ?? '—';

  function renderFeatured(item: QueueItem): ReactNode {
    if (item.kind === 'chore') {
      const row = item.chore;
      const person = personFor(row.claimed_by);
      return (
        <ApprovalCard
          instance={row}
          claimer={{
            id: row.claimed_by ?? '',
            short_name: row.claimed_by_short_name ?? person?.name ?? '',
            // #155: barnets ekte personfarge fra /api/members/basic, ikke
            // en fast 'm-terracotta'. Ukjent medlem gir standardfargen.
            slot: person?.slot ?? asColorSlot(null),
          }}
          approving={actions.approve.isPending && actions.approve.variables === row.id}
          rejecting={
            actions.reject.isPending && actions.reject.variables?.instanceId === row.id
          }
          {...(now ? { now } : {})}
          onApprove={(id) => actions.approve.mutate(id)}
          onReject={(id: string, reason: string, handlers: MutateHandlers) =>
            actions.reject.mutate({ instanceId: id, reason }, handlers)
          }
        />
      );
    }
    if (item.kind === 'proposal') {
      const p = item.proposal;
      const person = personFor(p.proposed_by);
      return (
        <ProposalCard
          proposal={p}
          proposerName={person?.name ?? ''}
          {...(person ? { proposerSlot: person.slot } : {})}
          approving={approveProposal.isPending && approveProposal.variables?.id === p.id}
          rejecting={rejectProposal.isPending && rejectProposal.variables?.id === p.id}
          onApprove={(id: string, overrides: ApproveProposalOverrides) =>
            approveProposal.mutate({ id, overrides })
          }
          onReject={(id: string, reason: string) =>
            rejectProposal.mutate(reason ? { id, reason } : { id })
          }
        />
      );
    }
    return (
      <RewardClaimCard claim={item.claim} rewardTitle={rewardTitle(item.claim.reward_id)} />
    );
  }

  function renderRow(item: QueueItem): ReactNode {
    const common = {
      'data-approval-item': item.id,
      onSelect: () => select(item.id),
    };
    const wait =
      (waitingHours(item.at, wallClock) ?? 0) >= 24 ? (
        <WaitChip at={item.at} now={wallClock} />
      ) : null;
    if (item.kind === 'chore') {
      const row = item.chore;
      const person = personFor(row.claimed_by);
      const name = row.claimed_by_short_name ?? person?.name ?? '';
      return (
        <QueueRow
          key={item.id}
          {...common}
          data-testid="approval-row"
          lead={
            <InitialAvatar
              name={name || '?'}
              colorSlot={person?.slot ?? asColorSlot(null)}
              size={36}
              decorative
            />
          }
          title={row.title_snapshot}
          meta={
            <>
              {t('approvals.row.choreMeta', { name, n: row.point_value_snapshot })}
              {row.completed_at ? (
                <>
                  {' · '}
                  <RelativeTime instant={row.completed_at} />
                </>
              ) : null}
            </>
          }
          trailing={wait}
        />
      );
    }
    if (item.kind === 'proposal') {
      const p = item.proposal;
      const person = personFor(p.proposed_by);
      const name = person?.name || t('approvals.proposals.unknownKid');
      const cadence = recurrenceLabel(p.recurrence, t);
      return (
        <QueueRow
          key={item.id}
          {...common}
          data-testid="approval-row-proposal"
          lead={
            <InitialAvatar
              name={name}
              colorSlot={person?.slot ?? asColorSlot(null)}
              size={36}
              decorative
            />
          }
          title={t('approvals.row.proposal', { title: p.title })}
          meta={
            cadence
              ? t('approvals.row.proposalMetaCadence', {
                  cadence,
                  name,
                  n: p.point_value,
                })
              : t('approvals.row.proposalMeta', { name, n: p.point_value })
          }
          trailing={wait}
        />
      );
    }
    const c = item.claim;
    return (
      <QueueRow
        key={item.id}
        {...common}
        data-testid="approval-row-reward"
        lead={<RewardTile />}
        title={t('approvals.row.reward', { title: rewardTitle(c.reward_id) })}
        meta={
          <>
            {t('approvals.row.rewardCost', { cost: c.point_cost_at_claim })}
            {' · '}
            <RelativeTime instant={c.claimed_at} />
          </>
        }
        trailing={wait}
      />
    );
  }

  const filterCount = (key: Filter): number => (key === 'all' ? items.length : counts[key]);

  return (
    <div data-testid="approvals-panel" className="flex flex-col gap-3">
      <header data-testid="approvals-header" className="flex items-center gap-3">
        <MeAvatarLink />
        <div className="min-w-0 flex-1">
          <h1 className="m-0 truncate text-heading-m font-extrabold tracking-tight text-ink">
            {t('nav.approvals')}
          </h1>
          <p data-testid="approvals-summary" className="m-0 mt-0.5 truncate text-control text-ink-2">
            {t('approvals.queue.summary', { count: items.length })}
          </p>
        </div>
        {over24 > 0 ? (
          <span
            data-testid="approvals-over-24h"
            className="inline-flex h-8 shrink-0 items-center rounded-pill bg-warn-bg px-3 text-caption font-extrabold text-warn"
          >
            {t('approvals.queue.over24h', { count: over24 })}
          </span>
        ) : null}
        {/* #187: tilbakemelding fra Godkjenn, med bildet av køen. */}
        <FeedbackWidget variant="icon" />
      </header>

      <div
        role="group"
        aria-label={t('approvals.queue.filterLabel')}
        data-testid="approvals-filters"
        // Én rad som skissen; blir den for bred, ruller den sidelengs i stedet
        // for å brekke (til kanten av skjermen, derfor -mx-5 px-5).
        className="no-scrollbar -mx-5 flex gap-1.5 overflow-x-auto px-5 md:-mx-8 md:px-8"
      >
        {FILTERS.map((key) => (
          <Chip
            key={key}
            selected={filter === key}
            data-testid={`approvals-filter-${key}`}
            onClick={() => setFilter(key)}
          >
            {/* Ett barn, så Chip sin gap ikke legges mellom navn og tall. */}
            <span>
              {t(`approvals.queue.filter.${key}`)}{' '}
              <span className="tnum">{filterCount(key)}</span>
            </span>
          </Chip>
        ))}
      </div>

      {isLoading ? null : featured === null ? (
        <div data-testid={filter === 'reward' ? 'reward-approvals-empty' : 'approvals-empty'}>
          <Card variant="soft">
            <EmptyState
              variant="inline"
              heading={t(
                filter === 'reward' ? 'rewards.empty.approvals.heading' : 'approvals.empty.heading',
              )}
              body={t(filter === 'reward' ? 'rewards.empty.approvals.body' : 'approvals.empty.body')}
            />
          </Card>
        </div>
      ) : (
        <section data-testid="approvals-list" className="flex flex-col gap-3">
          <div
            ref={featuredRef}
            data-approval-item={featured.id}
            data-testid="approvals-featured"
            className={highlightId === featured.id ? 'approval-card-highlight' : undefined}
          >
            {/* Nøkkelen er elementets id: et nytt element i det store kortet får
                ferskt avvisningspanel og poengvalg. */}
            <div key={featured.id}>{renderFeatured(featured)}</div>
          </div>

          {rest.length > 0 ? (
            <>
              <h2 className="m-0 mt-0.5 text-label font-bold tracking-wider text-ink-2 uppercase">
                {t('approvals.queue.rest')}
              </h2>
              <ul
                data-testid="approvals-rest"
                className={`${GLASS_SURFACE} m-0 list-none rounded-row px-3.5 py-1`}
              >
                {rest.map(renderRow)}
              </ul>
            </>
          ) : null}
        </section>
      )}

      {filter === 'reward' ? (
        <Card variant="soft">
          <RewardTemplateForm />
        </Card>
      ) : null}
    </div>
  );
}
